/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  User, Bell, Palette, Globe, Shield,
  Phone, Eye, EyeOff, Moon, Sun, Languages,
  Trash2, LogOut, ChevronRight, Check, Lock,
  Volume2, MessageCircle, Users, Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { usersAPI } from '../utils/api';
import './SettingsPage.css';

const SECTIONS = [
  { key:'account',       icon:User,           label:'Account'            },
  { key:'notifications', icon:Bell,           label:'Notifications'      },
  { key:'appearance',    icon:Palette,        label:'Appearance'         },
  { key:'privacy',       icon:Shield,         label:'Privacy & Security' },
  { key:'language',      icon:Languages,      label:'Language & Translate'},
  { key:'chat',          icon:MessageCircle,  label:'Chats'              },
];

function Toggle({ on, onToggle }) {
  return (
    <button className={`toggle-btn ${on?'on':''}`} onClick={onToggle}>
      <span className="toggle-knob"/>
    </button>
  );
}

export default function SettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const toast   = useToast();
  const { isDark, toggleTheme } = useTheme();
  const [section, setSection] = useState('account');
  const [saving,  setSaving]  = useState(false);

  // Account form
  const [form, setForm] = useState({
    displayName: user?.displayName || '',
    bio:         user?.bio         || '',
    website:     user?.website     || '',
    location:    user?.location    || '',
    status:      user?.status      || 'Hey there! I am using Nexa.',
  });

  // Notification settings
  const [notifs, setNotifs] = useState({
    messages:   true,
    mentions:   true,
    newFollower:true,
    groupMsg:   true,
    callNotif:  true,
    storyView:  false,
    emailNotif: false,
    sound:      true,
    vibrate:    true,
    preview:    true,
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    lastSeenVisible:  true,
    readReceipts:     true,
    profileVisible:   true,
    whoCanMessage:   'everyone',
    whoCanSeePhone:  'contacts',
    twoFA:            false,
  });

  // Chat settings
  const [chatSettings, setChatSettings] = useState({
    enterToSend:    true,
    mediaAutoSave:  false,
    disappearDefault: 0,
    fontSize:       'medium',
    wallpaper:      'default',
  });

  const saveAccount = async () => {
    setSaving(true);
    try {
      const { data } = await usersAPI.updateProfile(form);
      updateUser(data);
      toast.success('Profile saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const renderSection = () => {
    switch(section) {

      case 'account': return (
        <div className="settings-section">
          <h2>Account</h2>

          <div className="settings-card">
            <h3>Profile Info</h3>
            <div className="form-group">
              <label>Display Name</label>
              <input value={form.displayName} onChange={e=>setForm(p=>({...p,displayName:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea rows={3} maxLength={200} value={form.bio}
                placeholder="Tell people about yourself..."
                onChange={e=>setForm(p=>({...p,bio:e.target.value}))}/>
              <span style={{ fontSize:'0.72rem', color:'var(--text-muted)', textAlign:'right', display:'block' }}>
                {form.bio.length}/200
              </span>
            </div>
            <div className="form-group">
              <label>Status</label>
              <input value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} maxLength={100}/>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input type="url" placeholder="https://" value={form.website}
                onChange={e=>setForm(p=>({...p,website:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label>Location (city, country)</label>
              <input placeholder="Hyderabad, India" value={form.location}
                onChange={e=>setForm(p=>({...p,location:e.target.value}))}/>
            </div>
            <button className="btn btn-primary" onClick={saveAccount} disabled={saving}>
              {saving ? <span className="loader" style={{width:15,height:15,borderWidth:2}}/> : <><Check size={15}/> Save Changes</>}
            </button>
          </div>

          <div className="settings-card">
            <h3>Phone Number</h3>
            <div className="settings-info-row">
              <Phone size={16}/>
              <span>{user?.phone || 'Not set'}</span>
              <button className="btn btn-secondary btn-sm" onClick={()=>toast.info('Change phone: verify new number via OTP')}>
                Change
              </button>
            </div>
          </div>

          <div className="settings-card danger-zone">
            <h3>Danger Zone</h3>
            <button className="btn btn-danger w-full" onClick={()=>{ if(window.confirm('Log out?')) logout(); }}>
              <LogOut size={15}/> Sign Out
            </button>
            <button className="btn btn-danger w-full" style={{marginTop:8}} onClick={()=>toast.error('Account deletion — contact support')}>
              <Trash2 size={15}/> Delete Account
            </button>
          </div>
        </div>
      );

      case 'notifications': return (
        <div className="settings-section">
          <h2>Notifications</h2>
          <div className="settings-card">
            <h3>Message Notifications</h3>
            {[
              { key:'messages',    label:'New messages',       sub:'Get notified for new DMs' },
              { key:'mentions',    label:'Mentions',           sub:'When someone @mentions you' },
              { key:'groupMsg',    label:'Group messages',     sub:'Notifications from groups' },
              { key:'callNotif',   label:'Incoming calls',     sub:'Voice and video calls' },
              { key:'newFollower', label:'New followers',      sub:'When someone follows you' },
              { key:'storyView',   label:'Story views',        sub:'When someone views your story' },
            ].map(item => (
              <div key={item.key} className="settings-toggle-row">
                <div>
                  <p className="settings-row-label">{item.label}</p>
                  <p className="settings-row-sub">{item.sub}</p>
                </div>
                <Toggle on={notifs[item.key]} onToggle={()=>setNotifs(p=>({...p,[item.key]:!p[item.key]}))}/>
              </div>
            ))}
          </div>
          <div className="settings-card">
            <h3>Sound & Vibration</h3>
            {[
              { key:'sound',   label:'Notification sound', sub:'Play sound for new notifications' },
              { key:'vibrate', label:'Vibration',          sub:'Vibrate for notifications' },
              { key:'preview', label:'Message preview',    sub:'Show message content in notification' },
            ].map(item => (
              <div key={item.key} className="settings-toggle-row">
                <div>
                  <p className="settings-row-label">{item.label}</p>
                  <p className="settings-row-sub">{item.sub}</p>
                </div>
                <Toggle on={notifs[item.key]} onToggle={()=>setNotifs(p=>({...p,[item.key]:!p[item.key]}))}/>
              </div>
            ))}
          </div>
        </div>
      );

      case 'appearance': return (
        <div className="settings-section">
          <h2>Appearance</h2>
          <div className="settings-card">
            <h3>Theme</h3>
            <div className="theme-options">
              <button className={`theme-option ${isDark?'active':''}`} onClick={()=>!isDark&&toggleTheme()}>
                <Moon size={20}/>
                <span>Dark</span>
                {isDark && <Check size={14} className="theme-check"/>}
              </button>
              <button className={`theme-option ${!isDark?'active':''}`} onClick={()=>isDark&&toggleTheme()}>
                <Sun size={20}/>
                <span>Light</span>
                {!isDark && <Check size={14} className="theme-check"/>}
              </button>
            </div>
          </div>
          <div className="settings-card">
            <h3>Chat Wallpaper</h3>
            <div className="wallpaper-grid">
              {['default','gradient-purple','gradient-blue','gradient-green','dark-minimal','light-clean'].map(w => (
                <button key={w}
                  className={`wallpaper-option ${chatSettings.wallpaper===w?'active':''}`}
                  onClick={()=>{setChatSettings(p=>({...p,wallpaper:w}));toast.success('Wallpaper set!');}}
                  style={{
                    background: w==='gradient-purple' ? 'linear-gradient(135deg,#6C63FF,#FF4D8D)'
                              : w==='gradient-blue'   ? 'linear-gradient(135deg,#00B4D8,#6C63FF)'
                              : w==='gradient-green'  ? 'linear-gradient(135deg,#00D68F,#00B4D8)'
                              : w==='dark-minimal'    ? '#111'
                              : w==='light-clean'     ? '#F5F5F7'
                              : 'var(--bg-base)'
                  }}>
                  {chatSettings.wallpaper===w && <Check size={16} color="white"/>}
                </button>
              ))}
            </div>
          </div>
          <div className="settings-card">
            <h3>Font Size</h3>
            <div className="font-size-options">
              {['small','medium','large'].map(size => (
                <button key={size}
                  className={`font-size-btn ${chatSettings.fontSize===size?'active':''}`}
                  onClick={()=>{setChatSettings(p=>({...p,fontSize:size}));toast.success(`Font size: ${size}`);}}
                  style={{ fontSize: size==='small'?'0.8rem':size==='large'?'1.05rem':'0.9rem' }}>
                  Aa {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      );

      case 'privacy': return (
        <div className="settings-section">
          <h2>Privacy & Security</h2>
          <div className="settings-card">
            <h3>Who can see my info</h3>
            <div className="settings-select-row">
              <div>
                <p className="settings-row-label">Last seen & Online</p>
                <p className="settings-row-sub">Control who sees when you were last active</p>
              </div>
              <select value={privacy.whoCanSeePhone} onChange={e=>setPrivacy(p=>({...p,whoCanSeePhone:e.target.value}))}>
                <option value="everyone">Everyone</option>
                <option value="contacts">My Contacts</option>
                <option value="nobody">Nobody</option>
              </select>
            </div>
            <div className="settings-select-row">
              <div>
                <p className="settings-row-label">Who can message me</p>
                <p className="settings-row-sub">Limit who can start a conversation</p>
              </div>
              <select value={privacy.whoCanMessage} onChange={e=>setPrivacy(p=>({...p,whoCanMessage:e.target.value}))}>
                <option value="everyone">Everyone</option>
                <option value="contacts">My Contacts</option>
                <option value="nobody">Nobody</option>
              </select>
            </div>
          </div>

          <div className="settings-card">
            <h3>Chat Privacy</h3>
            {[
              { key:'lastSeenVisible', label:'Show last seen',     sub:'Let others see when you were last online' },
              { key:'readReceipts',    label:'Read receipts',      sub:'Let others know when you read their messages' },
              { key:'profileVisible',  label:'Public profile',     sub:'Anyone can find your profile' },
            ].map(item => (
              <div key={item.key} className="settings-toggle-row">
                <div>
                  <p className="settings-row-label">{item.label}</p>
                  <p className="settings-row-sub">{item.sub}</p>
                </div>
                <Toggle on={privacy[item.key]} onToggle={()=>setPrivacy(p=>({...p,[item.key]:!p[item.key]}))}/>
              </div>
            ))}
          </div>

          <div className="settings-card">
            <h3>Security</h3>
            <div className="settings-toggle-row">
              <div>
                <p className="settings-row-label">Two-Factor Authentication</p>
                <p className="settings-row-sub">Add extra security to your account</p>
              </div>
              <Toggle on={privacy.twoFA} onToggle={()=>{setPrivacy(p=>({...p,twoFA:!p.twoFA}));toast.info('2FA: set up via SMS or authenticator app');}}/>
            </div>
            <div className="e2e-info-box">
              <Lock size={16} style={{color:'var(--accent-green)',flexShrink:0}}/>
              <div>
                <p style={{fontWeight:600,fontSize:'0.85rem'}}>End-to-End Encryption</p>
                <p style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginTop:2}}>
                  All your messages and calls are protected with end-to-end encryption using AES-256. Not even Nexa can read them.
                </p>
              </div>
            </div>
          </div>
        </div>
      );

      case 'language': return (
        <div className="settings-section">
          <h2>Language & Translate</h2>
          <div className="settings-card">
            <h3>App Language</h3>
            <p className="settings-row-sub" style={{marginBottom:12}}>Choose your language for the Nexa interface</p>
            <select defaultValue="en" onChange={e=>toast.info(`Language set to ${e.target.value} — restart to apply`)}>
              {[
                ['en','🇬🇧 English'],['te','🇮🇳 Telugu'],['hi','🇮🇳 Hindi'],
                ['ta','🇮🇳 Tamil'],['es','🇪🇸 Spanish'],['fr','🇫🇷 French'],
                ['de','🇩🇪 German'],['ja','🇯🇵 Japanese'],['ko','🇰🇷 Korean'],
                ['ar','🇸🇦 Arabic'],['zh','🇨🇳 Chinese'],['pt','🇧🇷 Portuguese'],
                ['ru','🇷🇺 Russian'],['id','🇮🇩 Indonesian'],['tr','🇹🇷 Turkish'],
              ].map(([code,label])=>(
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div className="settings-card">
            <h3>Message Translation</h3>
            {[
              { label:'Auto-detect foreign messages', sub:'Show translate button on messages in other languages', on:true },
              { label:'Auto-translate incoming',      sub:'Automatically translate messages to your language',    on:false },
            ].map((item,i) => (
              <div key={i} className="settings-toggle-row">
                <div>
                  <p className="settings-row-label">{item.label}</p>
                  <p className="settings-row-sub">{item.sub}</p>
                </div>
                <Toggle on={item.on} onToggle={()=>toast.info('Translation preference saved')}/>
              </div>
            ))}
          </div>
        </div>
      );

      case 'chat': return (
        <div className="settings-section">
          <h2>Chats</h2>
          <div className="settings-card">
            <h3>Chat Settings</h3>
            <div className="settings-toggle-row">
              <div>
                <p className="settings-row-label">Enter key sends message</p>
                <p className="settings-row-sub">Press Enter to send (Shift+Enter for new line)</p>
              </div>
              <Toggle on={chatSettings.enterToSend} onToggle={()=>setChatSettings(p=>({...p,enterToSend:!p.enterToSend}))}/>
            </div>
            <div className="settings-toggle-row">
              <div>
                <p className="settings-row-label">Auto-save media</p>
                <p className="settings-row-sub">Automatically save received photos and videos</p>
              </div>
              <Toggle on={chatSettings.mediaAutoSave} onToggle={()=>setChatSettings(p=>({...p,mediaAutoSave:!p.mediaAutoSave}))}/>
            </div>
          </div>
          <div className="settings-card">
            <h3>Disappearing Messages</h3>
            <p className="settings-row-sub" style={{marginBottom:12}}>Default timer for all new chats</p>
            <select value={chatSettings.disappearDefault}
              onChange={e=>{setChatSettings(p=>({...p,disappearDefault:parseInt(e.target.value)}));toast.success('Disappear timer updated');}}>
              <option value={0}>Off</option>
              <option value={86400}>24 hours</option>
              <option value={604800}>7 days</option>
              <option value={2592000}>30 days</option>
            </select>
          </div>
          <div className="settings-card">
            <h3>Storage</h3>
            <div className="storage-bar-wrap">
              <div className="storage-bar">
                <div className="storage-used" style={{width:'35%'}}/>
              </div>
              <p style={{fontSize:'0.78rem',color:'var(--text-muted)',marginTop:6}}>
                350 MB used of unlimited cloud storage
              </p>
            </div>
            <button className="btn btn-secondary btn-sm" style={{marginTop:10}}
              onClick={()=>toast.info('Clearing cached media...')}>
              Clear Cache
            </button>
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-sidebar">
        <div style={{padding:'16px 14px 10px',borderBottom:'1px solid var(--border)'}}>
          <h2 style={{fontSize:'1.05rem',fontWeight:700}}>Settings</h2>
        </div>

        {/* Profile preview */}
        <div className="settings-profile-preview">
          <div className="settings-avatar-wrap">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username||'user'}`}
              alt="" className="settings-avatar"/>
            <span className="settings-online-dot"/>
          </div>
          <div>
            <p style={{fontWeight:600,fontSize:'0.9rem'}}>{user?.displayName}</p>
            <p style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>@{user?.username}</p>
          </div>
        </div>

        <nav className="settings-nav">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button key={s.key}
                className={`settings-nav-item ${section===s.key?'active':''}`}
                onClick={()=>setSection(s.key)}>
                <Icon size={17}/>
                <span>{s.label}</span>
                <ChevronRight size={14} style={{marginLeft:'auto',opacity:0.4}}/>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="settings-main">
        {renderSection()}
      </div>
    </div>
  );
}
